import { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface CountryPoint {
  name: string;
  count: number;
  lat: number;
  lng: number;
}

interface GlobeVisualizationProps {
  countries: CountryPoint[];
  activeVisitors: number;
  originGeo?: { lat: number; lng: number } | null;
}

const COUNTRY_NAMES: Record<string, string> = {
  SA:'السعودية',AE:'الإمارات',KW:'الكويت',BH:'البحرين',QA:'قطر',OM:'عُمان',
  EG:'مصر',JO:'الأردن',LB:'لبنان',IQ:'العراق',SY:'سوريا',PS:'فلسطين',
  YE:'اليمن',SD:'السودان',LY:'ليبيا',TN:'تونس',DZ:'الجزائر',MA:'المغرب',
  US:'أمريكا',GB:'بريطانيا',DE:'ألمانيا',FR:'فرنسا',TR:'تركيا',IN:'الهند',
  PK:'باكستان',MY:'ماليزيا',ID:'إندونيسيا',CN:'الصين',JP:'اليابان',
  BR:'البرازيل',MX:'المكسيك',AU:'أستراليا',CA:'كندا',RU:'روسيا',
  KR:'كوريا',NG:'نيجيريا',ZA:'جنوب أفريقيا',
};

const COUNTRY_LABELS = [
  { lat:23.8,lng:45.1,name:'السعودية',size:1.2 },{ lat:23.5,lng:54.5,name:'الإمارات',size:0.8 },
  { lat:29.3,lng:47.5,name:'الكويت',size:0.7 },{ lat:26.1,lng:50.6,name:'البحرين',size:0.6 },
  { lat:25.3,lng:51.2,name:'قطر',size:0.6 },{ lat:21.0,lng:57.0,name:'عُمان',size:0.8 },
  { lat:27.0,lng:30.0,name:'مصر',size:1.0 },{ lat:31.2,lng:36.8,name:'الأردن',size:0.7 },
  { lat:33.0,lng:43.5,name:'العراق',size:0.9 },{ lat:35.0,lng:38.5,name:'سوريا',size:0.7 },
  { lat:39.0,lng:35.0,name:'تركيا',size:0.9 },{ lat:32.5,lng:53.5,name:'إيران',size:0.9 },
  { lat:54.0,lng:-2.0,name:'بريطانيا',size:0.8 },{ lat:46.5,lng:2.5,name:'فرنسا',size:0.8 },
  { lat:51.0,lng:10.5,name:'ألمانيا',size:0.8 },{ lat:42.5,lng:12.5,name:'إيطاليا',size:0.8 },
  { lat:35.0,lng:105.0,name:'الصين',size:1.0 },{ lat:22.0,lng:80.0,name:'الهند',size:1.0 },
  { lat:39.0,lng:-98.0,name:'أمريكا',size:1.0 },{ lat:-10.0,lng:-52.0,name:'البرازيل',size:1.0 },
  { lat:-25.0,lng:134.0,name:'أستراليا',size:0.9 },{ lat:61.0,lng:100.0,name:'روسيا',size:1.2 },
];

export function GlobeVisualization({ countries, activeVisitors, originGeo }: GlobeVisualizationProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const globeRef = useRef<any>(null);
  const labelLayerRef = useRef<HTMLDivElement>(null);
  const labelDivsRef = useRef<HTMLDivElement[]>([]);
  const activeLabelsRef = useRef<any[]>([]);
  const [ready, setReady] = useState(false);

  // Init globe
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let cancelled = false;
    let rafId = 0;

    const initGlobe = () => {
      const GlobeFn = (window as any).Globe;
      if (!GlobeFn || !container || cancelled) return;

      container.innerHTML = '';
      if (labelLayerRef.current) labelLayerRef.current.innerHTML = '';
      labelDivsRef.current = [];
      activeLabelsRef.current = [];

      const globe = GlobeFn()(container)
        .backgroundColor('rgba(0,0,0,0)')
        .showAtmosphere(true)
        .atmosphereColor('rgba(60,80,140,0.25)')
        .atmosphereAltitude(0.15)
        .showGlobe(true)
        .globeImageUrl('')
        .labelsData([])
        .htmlElementsData([])
        .htmlLat((d: any) => d.lat)
        .htmlLng((d: any) => d.lng)
        .htmlAltitude(0.02)
        .htmlElement((d: any) => {
          const el = document.createElement('div');
          if (d.count > 1) {
            el.textContent = String(d.count);
            el.style.cssText = 'background:#10b981;color:#fff;font-size:9px;font-weight:800;min-width:16px;height:16px;border-radius:8px;display:flex;align-items:center;justify-content:center;padding:0 3px;font-family:Tajawal;border:2px solid #0a0e1e;pointer-events:none';
          } else {
            el.style.display = 'none';
          }
          return el;
        })
        .pointsData([])
        .pointLat((d: any) => d.lat)
        .pointLng((d: any) => d.lng)
        .pointColor(() => '#10ffa0')
        .pointAltitude(0.015)
        .pointRadius((d: any) => Math.min(0.25, 0.1 + (d.count || 1) * 0.025))
        .ringsData([])
        .ringLat((d: any) => d.lat)
        .ringLng((d: any) => d.lng)
        .ringColor(() => (t: number) => `rgba(16,255,160,${1 - t})`)
        .ringMaxRadius(1.2)
        .ringPropagationSpeed(1.2)
        .ringRepeatPeriod(1400)
        .ringAltitude(0.012)
        .arcsData([])
        .arcStartLat((d: any) => d.startLat)
        .arcStartLng((d: any) => d.startLng)
        .arcEndLat((d: any) => d.endLat)
        .arcEndLng((d: any) => d.endLng)
        .arcColor((d: any) => d.color)
        .arcAltitude(0.35)
        .arcStroke(2.2)
        .arcDashLength(0.5)
        .arcDashGap(0.1)
        .arcDashAnimateTime(1600);

      try {
        const mat = globe.globeMaterial();
        mat.color.set(0x0a0e1e);
        mat.emissive.set(0x060810);
        mat.emissiveIntensity = 0.4;
        globe.renderer().setClearColor(0x000000, 0);
      } catch {}

      globe.pointOfView({ lat: 24.5, lng: 46, altitude: 2.5 }, 0);
      globe.controls().autoRotate = true;
      globe.controls().autoRotateSpeed = 0.4;
      globe.controls().enableZoom = true;
      globe.controls().minDistance = 150;
      globe.controls().maxDistance = 600;

      globeRef.current = globe;

      // Load polygons
      const loadPolygons = () => {
        if (cancelled) return;
        fetch('https://unpkg.com/world-atlas@2/countries-110m.json')
          .then((r) => r.json())
          .then((topology) => {
            if (cancelled) return;
            const topojson = (window as any).topojson;
            const features = topojson.feature(topology, topology.objects.countries).features;
            globe
              .polygonsData(features)
              .polygonLabel(() => false as any)
              .polygonCapCurvatureResolution(1)
              .polygonCapColor(() => 'rgba(0,0,0,0)')
              .polygonSideColor(() => 'rgba(0,0,0,0)')
              .polygonStrokeColor((d: any) => d.id === '682' ? 'rgba(59,130,246,0.6)' : 'rgba(255,255,255,0.05)')
              .polygonAltitude((d: any) => d.id === '682' ? 0.003 : 0.001)
              .onPolygonHover(() => {});
          })
          .catch(() => {});
      };

      if ((window as any).topojson) {
        loadPolygons();
      } else {
        const topoScript = document.createElement('script');
        topoScript.src = 'https://unpkg.com/topojson-client@3/dist/topojson-client.min.js';
        topoScript.onload = loadPolygons;
        document.head.appendChild(topoScript);
      }

      // Build labels
      const buildLabels = () => {
        if (cancelled) return;
        const alt = globe.pointOfView().altitude;
        const list: any[] = [];
        COUNTRY_LABELS.forEach((c) => {
          list.push({ lat: c.lat, lng: c.lng, name: c.name, fs: (alt > 1.5 ? c.size : c.size * 0.6) * 10, bold: true });
        });
        syncLabels(list);
      };

      const syncLabels = (list: any[]) => {
        const layer = labelLayerRef.current;
        if (!layer) return;
        while (layer.firstChild) layer.removeChild(layer.firstChild);
        const newDivs: HTMLDivElement[] = [];
        for (const item of list) {
          const el = document.createElement('div');
          el.style.cssText = 'position:absolute;font-family:Tajawal,sans-serif;white-space:nowrap;user-select:none;pointer-events:none;text-shadow:0 1px 4px rgba(0,0,0,0.8),0 0 12px rgba(0,0,0,0.5);transform:translate(-50%,-50%)';
          el.textContent = item.name;
          (el as any)._lat = item.lat;
          (el as any)._lng = item.lng;
          el.style.fontSize = Math.max(9, item.fs) + 'px';
          el.style.fontWeight = item.bold ? '700' : '500';
          el.style.color = item.bold ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.22)';
          layer.appendChild(el);
          newDivs.push(el);
        }
        labelDivsRef.current = newDivs;
        activeLabelsRef.current = list;
      };

      setTimeout(() => { if (!cancelled) buildLabels(); }, 300);

      globe.controls().addEventListener('change', () => {
        if (cancelled) return;
        clearTimeout((globe as any)._lt);
        (globe as any)._lt = setTimeout(() => { if (!cancelled) buildLabels(); }, 150);
      });

      // Label position loop
      let frame = 0;
      const tick = () => {
        if (cancelled) return;
        rafId = requestAnimationFrame(tick);
        frame++;
        if (frame % 2 !== 0) return;
        try {
          const pov = globe.pointOfView();
          const divs = labelDivsRef.current;
          for (let i = 0; i < divs.length; i++) {
            const el = divs[i];
            if (!el) continue;
            const dLat = Math.abs((el as any)._lat - pov.lat);
            let dLng = Math.abs((el as any)._lng - pov.lng);
            if (dLng > 180) dLng = 360 - dLng;
            const angDist = dLat + dLng;
            if (angDist > 120) { el.style.opacity = '0'; continue; }
            const coords = globe.getScreenCoords((el as any)._lat, (el as any)._lng, 0.005);
            if (!coords) { el.style.opacity = '0'; continue; }
            el.style.opacity = angDist > 80 ? '0.08' : angDist > 55 ? '0.15' : '0.7';
            el.style.left = Math.round(coords.x) + 'px';
            el.style.top = Math.round(coords.y) + 'px';
          }
        } catch {}
      };
      tick();

      // Resize
      const ro = new ResizeObserver((entries) => {
        for (const e of entries) {
          globe.width(e.contentRect.width);
          globe.height(Math.min(520, Math.max(340, e.contentRect.width * 0.65)));
        }
      });
      ro.observe(container);
      (globe as any)._ro = ro;

      setReady(true);
    };

    if ((window as any).Globe) {
      initGlobe();
    } else {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/globe.gl@2.45.3/dist/globe.gl.min.js';
      script.onload = () => { if (!cancelled) initGlobe(); };
      document.head.appendChild(script);
    }

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      if (globeRef.current) {
        try { (globeRef.current as any)._ro?.disconnect(); } catch {}
        try { globeRef.current.renderer().dispose(); } catch {}
        try { globeRef.current.scene().clear(); } catch {}
        globeRef.current = null;
      }
      if (container) container.innerHTML = '';
      if (labelLayerRef.current) labelLayerRef.current.innerHTML = '';
      labelDivsRef.current = [];
      activeLabelsRef.current = [];
    };
  }, []);

  // Update markers when countries data changes
  useEffect(() => {
    const globe = globeRef.current;
    if (!globe || !ready) return;

    const markers: any[] = [];
    const arcs: any[] = [];

    const originLat = originGeo?.lat ?? 24.7136;
    const originLng = originGeo?.lng ?? 46.6753;

    countries.forEach((c) => {
      markers.push({ lat: c.lat, lng: c.lng, count: c.count });
      if (Math.abs(c.lat - originLat) > 0.05 || Math.abs(c.lng - originLng) > 0.05) {
        arcs.push({
          startLat: originLat, startLng: originLng,
          endLat: c.lat, endLng: c.lng,
          color: ['rgba(16,255,160,1)', 'rgba(16,255,160,0.5)'],
        });
      }
    });

    globe.htmlElementsData(markers);
    globe.pointsData(markers);
    globe.ringsData(markers);
    globe.arcsData(arcs);
  }, [countries, ready, originGeo?.lat, originGeo?.lng]);

  return (
    <div className="rounded-xl border border-border-default bg-[#0a0e1e] overflow-hidden relative" style={{ minHeight: 340 }}>
      {/* Header overlay */}
      <div className="absolute top-0 inset-x-0 z-10 flex items-center justify-between px-5 pt-4 pb-2" style={{ background: 'linear-gradient(to bottom, rgba(10,14,30,0.95), transparent)' }}>
        <div className="flex items-center gap-2.5 text-white/80">
          <span className="text-xs">{countries.length} {t('globe.countries', 'Countries')}</span>
        </div>
        <div className="flex items-center gap-2.5 text-white/80">
          <span className="text-xs font-medium">
            {activeVisitors} {t('globe.activeVisitors', 'Active visitors')}
          </span>
          <div className="h-2 w-2 rounded-full bg-success animate-pulse" />
        </div>
      </div>

      {/* Globe container */}
      <div ref={containerRef} className="w-full" style={{ minHeight: 340 }} />

      {/* Label layer */}
      <div
        ref={labelLayerRef}
        className="absolute inset-0 pointer-events-none overflow-hidden"
        style={{ zIndex: 2 }}
      />

      {/* Bottom gradient */}
      <div className="absolute bottom-0 inset-x-0 h-10 pointer-events-none" style={{ background: 'linear-gradient(to top, rgba(10,14,30,0.9), transparent)' }} />

      {/* Loading state */}
      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#0a0e1e]">
          <div className="h-8 w-8 rounded-full border-2 border-accent border-t-transparent animate-spin" />
        </div>
      )}
    </div>
  );
}
